/**
 * chat_widget/src/widget/utils/formatterLang.ts
 */

import type { QuestionExamples, Tone } from "../types/formatterTypes";

export const welcomeLang = (
  agentName: string,
  lang: string,
  tone: Tone = "formal",
) => {
  switch (lang) {
    case "fr-FR":
      return tone === "formal"
        ? `Bonjour, je suis ${agentName}.\nJe peux vous aider dans la recherche de votre véhicule ou vous renseigner sur les services de la concession.`
        : `Salut, moi c'est ${agentName}.\nJe peux t’aider à trouver un véhicule ou te renseigner sur les services de la concession.`;

    case "en-US":
      return tone === "formal"
        ? `Hello, I'm ${agentName}.\nI can help you find a vehicle or provide information about the dealership’s services.`
        : `Hi, I'm ${agentName}.\nI can help you find a vehicle or tell you more about the dealership’s services.`;

    case "de-DE":
      return tone === "formal"
        ? `Hallo, ich bin ${agentName}.\nIch kann Ihnen bei der Fahrzeugsuche helfen oder Sie über die Dienstleistungen des Autohauses informieren.`
        : `Hallo, ich bin ${agentName}.\nIch kann dir bei der Fahrzeugsuche helfen oder dir die Services des Autohauses erklären.`;

    case "es-ES":
      return tone === "formal"
        ? `Hola, soy ${agentName}.\nPuedo ayudarle a encontrar un vehículo o informarle sobre los servicios del concesionario.`
        : `Hola, soy ${agentName}.\nPuedo ayudarte a encontrar un vehículo o informarte sobre los servicios del concesionario.`;

    case "it-IT":
      return tone === "formal"
        ? `Buongiorno, sono ${agentName}.\nPosso aiutarla a trovare un veicolo o fornirle informazioni sui servizi della concessionaria.`
        : `Ciao, sono ${agentName}.\nPosso aiutarti a trovare un veicolo o darti informazioni sui servizi della concessionaria.`;

    default:
      return `Bonjour, je suis ${agentName}.\nJe peux vous aider dans la recherche de votre véhicule ou vous renseigner sur les services de la concession.`;
  }
};

export const exampleLang = (lang: string) => {
  switch (lang) {
    case "fr-FR":
      return "Par exemple :";

    case "en-US":
      return "For example:";

    case "de-DE":
      return "Zum Beispiel:";

    case "es-ES":
      return "Por ejemplo:";

    case "it-IT":
      return "Per esempio:";

    default:
      return "Par exemple :";
  }
};

export const questionLang = (lang: string): QuestionExamples => {
  switch (lang) {
    case "fr-FR":
      return {
        question1:
          "Je cherche une voiture hybride avec un budget de moins de 25 000 €",
        question2: "Je cherche une citadine à moins de 15 000 €",
        question3: "Je souhaite estimer la reprise de ma voiture",
      };

    case "en-US":
      return {
        question1: "I'm looking for a hybrid car with a budget under €25,000",
        question2: "I'm looking for a city car under €15,000",
        question3: "I would like to estimate the trade-in value of my car",
      };

    case "de-DE":
      return {
        question1:
          "Ich suche ein Hybridfahrzeug mit einem Budget von unter 25.000 €",
        question2: "Ich suche einen Kleinwagen unter 15.000 €",
        question3:
          "Ich möchte den Inzahlungnahmewert meines Fahrzeugs schätzen lassen",
      };

    case "es-ES":
      return {
        question1:
          "Busco un coche híbrido con un presupuesto inferior a 25.000 €",
        question2: "Busco un coche urbano por menos de 15.000 €",
        question3: "Quiero estimar el valor de recompra de mi coche",
      };

    case "it-IT":
      return {
        question1: "Cerco un’auto ibrida con un budget inferiore a 25.000 €",
        question2: "Cerco un’auto cittadina a meno di 15.000 €",
        question3: "Vorrei stimare il valore di permuta della mia auto",
      };

    default:
      return {
        question1:
          "Je cherche une voiture hybride avec un budget de moins de 25 000 €",
        question2: "Je cherche une citadine à moins de 15 000 €",
        question3: "Je souhaite estimer la reprise de ma voiture",
      };
  }
};

export const buttonLang = (lang: string) => {
  switch (lang) {
    case "fr-FR":
      return `Envoyer`;
    case "en-US":
      return `Send`;
    case "de-DE":
      return `Schicken`;
    case "es-ES":
      return `Enviar`;
    case "it-IT":
      return `Inviare`;
    default:
      return `Envoyer`;
  }
};
export const inputLang = (lang: string, tone: Tone = "formal") => {
  switch (lang) {
    case "fr-FR":
      return tone === "formal"
        ? "Posez votre question..."
        : "Pose ta question...";

    case "en-US":
      // L’anglais ne marque pas vraiment la différence,
      // mais on garde le paramètre pour cohérence globale
      return "Ask your question...";

    case "de-DE":
      return tone === "formal"
        ? "Stellen Sie Ihre Frage..."
        : "Stell deine Frage...";

    case "es-ES":
      return tone === "formal"
        ? "Formule su pregunta..."
        : "Haz tu pregunta...";

    case "it-IT":
      return tone === "formal"
        ? "Inserisca la sua domanda..."
        : "Fai la tua domanda...";

    default:
      return tone === "formal"
        ? "Posez votre question..."
        : "Pose ta question...";
  }
};

export type EnergieKey = "ESSENCE" | "ELECTRIQUE" | "DIESEL" | "HYBRIDE";
export type EnergieMap = Record<EnergieKey, string>;
export const energieLang = (lang: string): EnergieMap => {
  switch (lang) {
    case "fr-FR":
      return {
        ESSENCE: "ESSENCE",
        ELECTRIQUE: "ELECTRIQUE",
        DIESEL: "DIESEL",
        HYBRIDE: "HYBRIDE",
      };
    case "en-US":
      return {
        ESSENCE: "PETROL",
        ELECTRIQUE: "ELECTRIC",
        DIESEL: "DIESEL",
        HYBRIDE: "HYBRID",
      };
    case "de-DE":
      return {
        ESSENCE: "BENZIN",
        ELECTRIQUE: "ELEKTRISCH",
        DIESEL: "DIESEL",
        HYBRIDE: "HYBRID",
      };
    case "es-ES":
      return {
        ESSENCE: "GASOLINA",
        ELECTRIQUE: "ELÉCTRICO",
        DIESEL: "DIÉSEL",
        HYBRIDE: "HÍBRIDO",
      };
    case "it-IT":
      return {
        ESSENCE: "BENZINA",
        ELECTRIQUE: "ELETTRICO",
        DIESEL: "DIESEL",
        HYBRIDE: "IBRIDO",
      };
    default:
      return {
        ESSENCE: "ESSENCE",
        ELECTRIQUE: "ELECTRIQUE",
        DIESEL: "DIESEL",
        HYBRIDE: "HYBRIDE",
      };
  }
};
export type boiteKey = "AUTOMATIQUE" | "MANUELLE";
export type BoiteMap = Record<boiteKey, string>;
export const boiteLang = (lang: string): BoiteMap => {
  switch (lang) {
    case "fr-FR":
      return {
        AUTOMATIQUE: "AUTOMATIQUE",
        MANUELLE: "MANUELLE",
      };
    case "en-US":
      return {
        AUTOMATIQUE: "AUTOMATIC",
        MANUELLE: "MANUAL",
      };
    case "de-DE":
      return {
        AUTOMATIQUE: "AUTOMATIK",
        MANUELLE: "MANUELL",
      };
    case "es-ES":
      return {
        AUTOMATIQUE: "AUTOMÁTICA",
        MANUELLE: "MANUAL",
      };
    case "it-IT":
      return {
        AUTOMATIQUE: "AUTOMATICA",
        MANUELLE: "MANUALE",
      };
    default:
      return {
        AUTOMATIQUE: "AUTOMATIQUE",
        MANUELLE: "MANUELLE",
      };
  }
};
